import {
  createController,
  currentMonthFirst,
  emptyOptionSearch,
  optionQueryAction,
  parseMonth,
} from '@eo/shared';

import {
  fetchClientRows,
  fetchEntryRows,
  fetchReport,
  searchClients,
  searchPeople,
  syncMonth,
} from './api/api';
import { clientListParams, entryListParams, parseDetailPane, parseSelection } from './helpers';
import type { CapacityState, LoadKeys, SyncPayload } from './types';

const initialState: CapacityState = {
  options: { clients: emptyOptionSearch(), people: emptyOptionSearch() },
  month: currentMonthFirst(),
  report: null,
  clientRows: [],
  clientsPagination: null,
  entryRows: [],
  entriesPagination: null,
  loadedFor: null,
};

// The report is the expensive part, so each piece is fetched again only when
// its own inputs changed: sorting the log or opening a client leaves the report
// as it is. A null `previous` fetches everything.
async function load(
  month: string,
  owner: string | undefined,
  query: Record<string, string>,
  previous: CapacityState | null,
) {
  // The log is detail someone asks for: it loads only when a client's or a
  // person's Time log is open.
  const selection = parseSelection(query);
  const entryParams =
    selection !== null && parseDetailPane(query.pane) === 'log'
      ? entryListParams(selection, query)
      : null;
  const clientParams = clientListParams(query);
  const keys: LoadKeys = {
    report: JSON.stringify([month, owner]),
    clients: JSON.stringify([month, owner, clientParams]),
    entries: entryParams && JSON.stringify([month, owner, entryParams]),
  };
  const fresh = (key: keyof LoadKeys) => previous?.loadedFor?.[key] !== keys[key];

  const [report, clients, entries] = await Promise.all([
    fresh('report') ? fetchReport(month, owner) : null,
    fresh('clients') ? fetchClientRows(month, owner, clientParams) : null,
    entryParams && fresh('entries') ? fetchEntryRows(month, owner, entryParams) : null,
  ]);
  return {
    loadedFor: keys,
    ...(report && { report }),
    ...(clients && { clientRows: clients.clients, clientsPagination: clients.pagination }),
    ...(entries && { entryRows: entries.entries, entriesPagination: entries.pagination }),
    ...(!entryParams && { entryRows: [], entriesPagination: null }),
  };
}

export const capacityController = createController<CapacityState>({
  name: 'capacity',
  state: initialState,
  fetchData: async ({ queryParams, data }) => {
    const params = (queryParams ?? {}) as Record<string, string>;
    const month = parseMonth(params.month);

    return {
      month,
      ...(await load(month, params.cfo, params, (data as CapacityState | undefined) ?? null)),
    };
  },
  asyncActions: {
    queryOptions: optionQueryAction<CapacityState>({
      clients: searchClients,
      people: searchPeople,
    }),
    // The refreshed report is refetched rather than patched in: the sync
    // rewrites entries, and every derived number is computed server-side.
    syncMonth: {
      action: async (payload) => {
        const { month, query } = payload as SyncPayload;
        await syncMonth(month);
        return load(month, query.cfo, query, null);
      },
      handlers: {
        onSuccess: (state, { action }) => ({
          ...state,
          ...(action as { payload: Omit<CapacityState, 'month'> }).payload,
        }),
      },
    },
  },
});
